/// <amd-module name="Wit/QueriesHub/Charts/Components/Configuration/ChartConfigurationForm" />

import * as React from "react";
import { DataServiceCapabilities, NameLabelPair } from "Tfs/Charting/Contracts/Reporting";
import { IObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { ChartProperty, ChartPropertyMap, ChartPropertyType, IChartTemplate } from "Wit/QueriesHub/Charts/Common/ChartTemplate";
import { IQueryDisplayColumn } from "Wit/QueriesHub/Common/Interfaces";
import { IQueryChartService } from "Wit/QueriesHub/Common/Services/IQueryChartService";
import * as Resources from "Wit/QueriesHub/Resources";

/**
 * Represents the configuration for an instance of a chart.
 */
export interface IChartConfiguration {
    name: string;
}
export interface IChartConfigurationFormProps {
    projectId: string;
    chartTemplate: IChartTemplate;
    isValid: IObservableValue<boolean>;
    chartProperties: IObservableValue<ChartPropertyMap>;
    name: IObservableValue<string>;
    capabilities: DataServiceCapabilities;
    queryColumns: IQueryDisplayColumn[];
}

/**
 * Form for editing a chart's configuration.
 */
export class ChartConfigurationForm extends VssComponent<IChartConfigurationFormProps> {
    private _groupableFields: IListBoxItem[];
    private _aggregatableFields: IListBoxItem[];
    private _aggregationFunctions: IListBoxItem[];
    private _historyRanges: IListBoxItem[];

    constructor(props: IChartConfigurationFormProps, context: IVssComponentContext) {
        super(props, context);

        const { capabilities, queryColumns } = this.props;

        const chartService = this.context.pageContext.getService<IQueryChartService>("IQueryChartService");
        this._groupableFields = chartService.getGroupableFields(capabilities, queryColumns).map(nameLabelPairToListBoxItem);
        this._aggregatableFields = chartService.getAggregatableFields(capabilities, queryColumns).map(nameLabelPairToListBoxItem);
        this._historyRanges = this.props.capabilities.historyRanges.map(r => ({ id: r.name, text: r.labelText }));

        if (this._aggregatableFields.length > 0) {
            this._aggregationFunctions = [...defaultAggregationFunctions, ...capabilities.numericalAggregationFunctions].map(
                nameLabelPairToListBoxItem
            );
        } else {
            this._aggregationFunctions = [...defaultAggregationFunctions].map(nameLabelPairToListBoxItem);
        }

        this.setDefaultProperties();
    }

    public render(): JSX.Element {
        return (
            <>
                <TextField
                    autoFocus
                    label={<RequiredLabel>{Resources.NameLabel}</RequiredLabel>}
                    onChange={(event, value) => {
                        this.props.name.value = value;
                    }}
                    required
                    value={this.props.name}
                />
                <Observer chartProperties={this.props.chartProperties}>
                    {({ chartProperties }: { chartProperties: ChartPropertyMap }) =>
                        this.props.chartTemplate.properties.map(p => this._renderProperty(p, chartProperties))
                    }
                </Observer>
            </>
        );
    }

    public componentDidMount() {
        super.componentDidMount();

        this._validate();
    }

    public componentDidUpdate(prevProps: IChartConfigurationFormProps, prevState: {}, snapshot: {}) {
        super.componentDidUpdate(prevProps, prevState, snapshot);

        // handle chart type change
        if (this.props.chartTemplate !== prevProps.chartTemplate) {
            this.setDefaultProperties();
            this._validate();
        }
    }

    /**
     * Sets any unset chart properties to their default values.
     */
    private setDefaultProperties() {
        const oldProperties = this.props.chartProperties.value;
        const newProperties: ChartPropertyMap = {};
        for (const p of this.props.chartTemplate.properties) {
            const val = oldProperties[p] ?? this._properties[p].defaultValue();
            if (val !== undefined) {
                (newProperties[p] as ChartPropertyType<typeof p>) = val;
            }
        }
        this.props.chartProperties.value = newProperties;
    }

    /**
     * Apply the changes in updatedProperties to the chart properties. If a property's value is the value undefined, remove that property.
     */
    private _onChange = (updatedProperties: ChartPropertyMap) => {
        const properties: ChartPropertyMap = { ...this.props.chartProperties.value, ...updatedProperties };

        for (const [k, v] of Object.entries(updatedProperties)) {
            if (v === undefined) {
                delete properties[k as keyof ChartPropertyMap];
            }
        }

        this.props.chartProperties.value = properties;
        this._validate();
    };

    private _validate(): boolean {
        let valid = true;

        if (!this.props.name.value || !this.props.chartTemplate.properties.every(p => this._validateProperty(p))) {
            valid = false;
        }

        if (valid !== this.props.isValid.value) {
            this.props.isValid.value = valid;
        }

        return true;
    }

    private _renderProperty(name: ChartProperty, chartProperties: ChartPropertyMap) {
        if (this._properties[name]) {
            const PropertyEditor = this._properties[name].render;
            return (
                <PropertyEditor
                    key={name}
                    aggregatableFields={this._aggregatableFields}
                    aggregationFunctions={this._aggregationFunctions}
                    chartProperties={chartProperties}
                    chartTemplate={this.props.chartTemplate}
                    groupableFields={this._groupableFields}
                    historyRanges={this._historyRanges}
                    onChange={this._onChange}
                />
            );
        } else {
            return <div key={name}>Unknown property {name}</div>;
        }
    }

    private _validateProperty(name: ChartProperty) {
        return !!this._properties[name]?.validate(this.props.chartProperties.value);
    }

    /**
     * list of all supported chart properties, containing methods for rendering form controls and validating the value
     * of that property, as well as the default value for that property
     */
    private readonly _properties: {
        [key in ChartProperty]: {
            render: (props: IPropertyEditorProps) => JSX.Element | null;
            validate: (chartProperties: ChartPropertyMap) => boolean;
            defaultValue: () => ChartPropertyType<key> | undefined;
        };
    } = {
        groupBy: {
            render: GroupBy,
            validate: chartProperties => chartProperties.groupBy !== undefined,
            defaultValue: () => undefined
        },
        measure: {
            render: Measure,
            validate: () => true,
            defaultValue: () => ({ aggregation: "count", propertyName: "" })
        },
        orderBy: {
            render: OrderBy,
            validate: () => true,
            defaultValue: () => ({
                direction: "ascending",
                propertyName: "value"
            })
        },
        series: {
            render: Series,
            validate: chartProperties => chartProperties.series !== undefined,
            defaultValue: () => undefined
        },
        historyRange: {
            render: HistoryRange,
            validate: () => true,
            defaultValue: () => this._historyRanges[0].id
        }
    };
}

interface IPropertyEditorProps {
    aggregatableFields: IListBoxItem[];
    aggregationFunctions: IListBoxItem[];
    chartTemplate: IChartTemplate;
    groupableFields: IListBoxItem[];
    historyRanges: IListBoxItem[];
    chartProperties: ChartPropertyMap;
    onChange: (updatedProperties: ChartPropertyMap) => void;
}

// Group by
function GroupBy({ chartProperties, chartTemplate, groupableFields, onChange }: IPropertyEditorProps) {
    const selection = useDropdownSelection(groupableFields, chartProperties.groupBy, -1);
    let label: string;
    let placeholder: string;
    if (chartTemplate.type === "PivotTable") {
        label = Resources.Columns;
        placeholder = Resources.ColumnsPlaceholder;
    } else if (chartTemplate.type === "StackAreaChart") {
        label = Resources.StackBy;
        placeholder = Resources.StackByPlaceholder;
    } else {
        label = Resources.GroupBy;
        placeholder = Resources.GroupByPlaceholder;
    }

    return (
        <PropertyView
            key="groupBy"
            label={label}
            required
            tooltip={Resources.GroupByTooltip} // tooltip is invariant
        >
            <Dropdown
                ariaLabel={label}
                items={groupableFields}
                placeholder={placeholder}
                selection={selection}
                onSelect={(_, item) => onChange({ groupBy: item.id })}
            />
        </PropertyView>
    );
}

// Aggregation
function Measure({ aggregatableFields, aggregationFunctions, chartProperties, onChange }: IPropertyEditorProps) {
    function getValue() {
        const aggregation = getSelectedId(aggregationFunctions, functionSelection)!;
        const field = getSelectedId(aggregatableFields, fieldSelection)!;
        return {
            aggregation,
            propertyName: aggregation === "count" ? "" : field
        };
    }
    const functionSelection = useDropdownSelection(aggregationFunctions, chartProperties.measure?.aggregation, 0);
    const fieldSelection = useDropdownSelection(aggregatableFields, chartProperties.measure?.propertyName, 0);
    const countableSelection = useDropdownSelection(countableItems, undefined, 0);

    return (
        <PropertyView key="measure" label={Resources.Aggregation} tooltip={Resources.AggregationTooltip}>
            <div className="flex-row flex-wrap flex-baseline rhythm-horizontal-4">
                <Dropdown
                    ariaLabel={Resources.Aggregation}
                    className="flex-grow"
                    items={aggregationFunctions}
                    selection={functionSelection}
                    onSelect={() => onChange({ measure: getValue() })}
                />
                <span>{Resources.AggregationOf}</span>
                <Observer aggregationFunctionSelection={functionSelection}>
                    {({ aggregationFunctionSelection }: { aggregationFunctionSelection: ISelectionRange[] }) => {
                        const countSelected = aggregationFunctionSelection[0]?.beginIndex === 0;
                        return (
                            <>
                                {/* 
                                    Dropdown doesn't care for having its selection prop changed, and DropdownSelections
                                    don't respond well to being changed from render methods, so we have two Dropdowns
                                    and toggle which one is displayed.
                                */}
                                <Dropdown
                                    ariaLabel={Resources.AggregationFieldLabel}
                                    className={css("flex-grow", countSelected && "hidden")}
                                    disabled={false}
                                    items={aggregatableFields}
                                    selection={fieldSelection}
                                    onSelect={() => onChange({ measure: getValue() })}
                                />
                                <Dropdown
                                    ariaLabel={Resources.AggregationFieldLabel}
                                    className={css("flex-grow", !countSelected && "hidden")}
                                    disabled={true}
                                    items={countableItems}
                                    selection={countableSelection}
                                />
                            </>
                        );
                    }}
                </Observer>
            </div>
        </PropertyView>
    );
}

// Sort
function OrderBy({ chartProperties, chartTemplate, onChange }: IPropertyEditorProps) {
    function getValue() {
        const orderBy = getSelectedId(orderByItems, orderBySelection)!;
        const direction = getSelectedId(directionItems, directionSelection)!;
        return {
            direction,
            propertyName: orderBy
        };
    }

    const orderBySelection = useDropdownSelection(orderByItems, chartProperties.orderBy?.propertyName, 1);
    const directionSelection = useDropdownSelection(directionItems, chartProperties.orderBy?.direction, 0);

    // orderBy is a required property for AreaChart, but is not shown to the user
    return chartTemplate.type === "AreaChart" ? null : (
        <PropertyView key="orderBy" label={Resources.Sort} tooltip={Resources.SortTooltip}>
            <div className="flex-row flex-wrap rhythm-horizontal-4">
                <Dropdown
                    ariaLabel={Resources.Sort}
                    className="flex-grow"
                    items={orderByItems}
                    selection={orderBySelection}
                    onSelect={() => onChange({ orderBy: getValue() })}
                />
                <Dropdown
                    ariaLabel={Resources.SortOrder}
                    className="flex-grow"
                    items={directionItems}
                    selection={directionSelection}
                    onSelect={() => onChange({ orderBy: getValue() })}
                />
            </div>
        </PropertyView>
    );
}

// "Y-axis"
function Series({ chartProperties, chartTemplate, groupableFields, onChange }: IPropertyEditorProps) {
    const selection = useDropdownSelection(groupableFields, chartProperties.series, -1);

    return (
        <PropertyView key="series" label={chartTemplate.type === "PivotTable" ? Resources.Rows : Resources.YAxis}>
            <Dropdown
                ariaLabel={chartTemplate.type === "PivotTable" ? Resources.Rows : Resources.YAxis}
                items={groupableFields}
                placeholder={chartTemplate.type === "PivotTable" ? Resources.RowsPlaceholder : Resources.YAxisPlaceholder}
                selection={selection}
                onSelect={(_, item) => onChange({ series: item.id })}
            />
        </PropertyView>
    );
}

// "Rolling period"
function HistoryRange({ chartProperties, historyRanges, onChange }: IPropertyEditorProps) {
    const selection = useDropdownSelection(historyRanges, chartProperties.historyRange, 0);

    return (
        <PropertyView key="historyRange" label={Resources.HistoryRangeLabel} tooltip={Resources.HistoryRangeTooltip}>
            <Dropdown
                ariaLabel={Resources.HistoryRangeLabel}
                items={historyRanges}
                selection={selection}
                onSelect={(_, item) => onChange({ historyRange: item.id })}
            />
        </PropertyView>
    );
}

/**
 * Hook to get a DropdownSelect object. On first invocation, select the item with id given in initialValue, or select
 * the item at defaultIndex if initialValue is not found or is undefined.
 */
function useDropdownSelection(items: IListBoxItem[], initialValue: string | undefined, defaultIndex: number) {
    const [selection] = React.useState(() => {
        const dds = new DropdownSelection();
        const toSelect = initialValue === undefined ? defaultIndex : items.findIndex(i => i.id === initialValue);
        if (toSelect === -1) {
            dds.clear();
        } else {
            dds.select(toSelect);
        }
        return dds;
    });
    return selection;
}

export function PropertyView({
    label,
    required,
    tooltip,
    children
}: React.PropsWithChildren<{ label: string; required?: boolean; tooltip?: string }>) {
    return (
        <label className="flex-column rhythm-vertical-8">
            <div className="flex-row flex-wrap justify-space-between">
                <span>{label}</span>
                <div className="flex-row rhythm-horizontal-4">
                    {required && <div className="secondary-text">{Resources.RequiredLabel}</div>}
                    <Icon ariaLabel={tooltip} tabIndex={0} iconName="Info" tooltipProps={tooltip === undefined ? undefined : { text: tooltip }} />
                </div>
            </div>
            {children}
        </label>
    );
}

function RequiredLabel(props: React.PropsWithChildren<{}>) {
    return (
        <div className="flex-row">
            <div className="flex-grow">{props.children}</div>
            <div className="secondary-text">{Resources.RequiredLabel}</div>
        </div>
    );
}

const nameLabelPairToListBoxItem = (nlp: NameLabelPair) => ({ id: nlp.name, text: nlp.labelText });

/**
 * Get the id of the selected IListBoxItem
 */
function getSelectedId(items: IListBoxItem[], selection: DropdownSelection): string | undefined {
    if (selection.value && selection.value.length > 0) {
        const idx = selection.value[0].beginIndex;
        if (idx < items.length) {
            return items[idx].id;
        }
    }
    return undefined;
}

/**
 * These aggregation functions are always available.
 */
const defaultAggregationFunctions: NameLabelPair[] = [
    {
        name: "count",
        labelText: Resources.AggregationCount
    }
];

const countableItems: IListBoxItem[] = [{ id: "workItems", text: Resources.AggregationWorkItems }];

const orderByItems: IListBoxItem[] = [
    { id: "label", text: Resources.SortByLabel },
    { id: "value", text: Resources.SortByValue }
];
const directionItems: IListBoxItem[] = [
    { id: "ascending", text: Resources.Ascending },
    { id: "descending", text: Resources.Descending }
];
